import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Cell, Pie, PieChart } from 'recharts';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';

import { DATE_PICKER_POPPER_MODIFIERS } from '../../../../util/DateUtil';
import { downloadFile, extractFileNameFromHeaders } from '../../../../util/DownloadUtil';

import { downloadParticipantReviewsReport, retrieveParticipantReviewsReport } from '../../../../services/ReportService';

import { SaveButton } from '../../../common/button/save-button/SaveButton';
import { Table } from '../../../common/table/Table';

import './participant-reviews.css';

const ALL_STUDIES = 'All Studies';
const ALL_STUDIES_OPTION = {
  label: ALL_STUDIES,
  value: ALL_STUDIES,
};

export const ParticipantReviews = ({
  facility,
  dates,
  resetFilters,
  context,
  setSpinnerText,
  showToaster,
  downloadEnabled,
}) => {
  const [heading, setHeading] = useState('');
  const [results, setResults] = useState([]);
  const [reportData, setReportData] = useState();
  const [selectedStudy, setSelectedStudy] = useState(ALL_STUDIES_OPTION);
  const [fromDate, setFromDate] = useState();
  const [toDate, setToDate] = useState();
  const [showTable, setShowTable] = useState(false);
  const [filterValues, setFilterValues] = useState({
    selectedStudy: ALL_STUDIES,
  });

  const graphData = useMemo(() => {
    if (!reportData) {
      return;
    }

    if (results?.length === 0) {
      return {
        results: [
          {
            name: 'Reviews with Queries',
            value: reportData.reviewsWithQueries,
            color: '#cd2027',
          },
          {
            name: 'Reviews with No Queries',
            value: reportData.reviewsWithoutQueries,
            color: '#e0ac25',
          },
        ],
        grandTotal: reportData.grandTotal,
      };
    }

    let resultsToFilter = results;
    if (ALL_STUDIES !== filterValues.selectedStudy) {
      resultsToFilter = resultsToFilter.filter((result) => result.study === filterValues.selectedStudy);
    }

    if (filterValues.fromDate) {
      resultsToFilter = resultsToFilter.filter((result) => new Date(result.date) >= filterValues.fromDate);
    }

    if (filterValues.toDate) {
      resultsToFilter = resultsToFilter.filter((result) => new Date(result.date) <= filterValues.toDate);
    }

    const withQueries = resultsToFilter.filter((result) => !result.noQueries).length;
    const withNoQueries = resultsToFilter.filter((result) => result.noQueries).length;

    return {
      results: [
        {
          name: 'Reviews with Queries',
          value: withQueries,
          color: '#cd2027',
        },
        {
          name: 'Reviews with No Queries',
          value: withNoQueries,
          color: '#e0ac25',
        },
      ],
      grandTotal: withNoQueries + withQueries,
    };
  }, [results, reportData, filterValues]);

  const studyOptions = useMemo(() => {
    return results.reduce((acc, currentValue) => {
      if (acc.some((studyOption) => studyOption.label === currentValue.study)) {
        return acc;
      }

      return [
        ...acc,
        {
          label: currentValue.study,
          value: currentValue.study,
        },
      ];
    }, []);
  }, [results]);

  const dateRestrictions = useMemo(() => {
    if (!dates) return {};

    const sortedDates = [...dates].sort((dateOne, dateTwo) => dateOne - dateTwo);

    const oldestYear = sortedDates[0];
    const earliestYear = sortedDates[sortedDates.length - 1];

    return {
      min: new Date(`01-01-${oldestYear}`),
      max: new Date(`12-31-${earliestYear}`),
    };
  }, [dates]);

  const generateReport = useCallback(async () => {
    setSpinnerText('Generating Report');

    const payload = {
      facilityExternalReference: facility.externalReference,
      dates,
    };
    const [data, error] = await retrieveParticipantReviewsReport(context.username, payload);

    if (!data) {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });

      setSpinnerText(undefined);
      return;
    }

    if (data.responseStatus === 'Success') {
      setHeading(data.heading);
      setReportData(data);
      setResults(data.participantReviewVOS);
    } else {
      showToaster(data);
    }

    setSpinnerText(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, facility, dates]);

  useEffect(() => {
    generateReport();
  }, [generateReport]);

  const onApplyFilterClick = () => {
    setFilterValues({
      selectedStudy: selectedStudy.value,
      fromDate,
      toDate,
    });
  };

  const handleDownloadExcelClick = async () => {
    setSpinnerText('Downloading Participant Reviews Report');

    const payload = {
      facilityExternalReference: facility.externalReference,
      dates,
    };
    const [data, headers, error] = await downloadParticipantReviewsReport(context.username, payload);

    if (data) {
      const name = extractFileNameFromHeaders(headers);
      downloadFile(data, name);
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }

    setSpinnerText(undefined);
  };

  return (
    <section className="participant-reviews-container">
      <div className="participant-reviews-content">
        <h1 className="participant-reviews-heading">{heading}</h1>
        {showTable ? (
          <Table
            tableData={results}
            tableTitle="Participant Reviews"
            maxRowCount={9}
            hiddenColumns={[]}
            searchEnabled
            filterEnabled
            stickyFirstRow
            horizontalScroll
          />
        ) : (
          <section className="participant-reviews-graph">
            <PieChart width={600} height={400}>
              <Pie
                data={graphData?.results}
                nameKey="name"
                dataKey="value"
                innerRadius={135}
                outerRadius={200}
                isAnimationActive={false}
                labelLine={false}
                label={<PiePieceLabel />}
              >
                {graphData?.results.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
            <div className="participant-reviews-graph-info">
              <div className="participant-reviews-graph-filters">
                <div className="input-container">
                  <label className="input-label">Study:</label>
                  <Select
                    options={[ALL_STUDIES_OPTION, ...studyOptions]}
                    onChange={(value) => setSelectedStudy(value)}
                    value={selectedStudy}
                    classNamePrefix="input-select"
                    closeMenuOnSelect
                  />
                </div>
                <div className="input-container">
                  <label className={'input-label'}>From Date:</label>
                  <DatePicker
                    selected={fromDate}
                    className="text-input"
                    dateFormat="dd-MM-yyyy"
                    showPopperArrow={false}
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    placeholderText="Select..."
                    onChange={(selectedDate) => setFromDate(selectedDate)}
                    popperModifiers={DATE_PICKER_POPPER_MODIFIERS}
                    minDate={dateRestrictions.min}
                    maxDate={dateRestrictions.max}
                  />
                  <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
                </div>
                <div className="input-container">
                  <label className={'input-label'}>To Date:</label>
                  <DatePicker
                    selected={toDate}
                    className="text-input"
                    dateFormat="dd-MM-yyyy"
                    showPopperArrow={false}
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    placeholderText="Select..."
                    onChange={(selectedDate) => setToDate(selectedDate)}
                    popperModifiers={DATE_PICKER_POPPER_MODIFIERS}
                    minDate={dateRestrictions.min}
                    maxDate={dateRestrictions.max}
                  />
                  <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
                </div>
                <SaveButton
                  title="Apply Filter"
                  onClick={onApplyFilterClick}
                  color="#077187"
                  border="1px solid #077187"
                />
              </div>
              <ChartLegend data={graphData} />
            </div>
          </section>
        )}
      </div>
      <div className="participant-reviews-buttons">
        <SaveButton
          title={`View as ${showTable ? 'Graph' : 'Table'}`}
          onClick={() => setShowTable(!showTable)}
          color="white"
          textColor="#077187"
          border="1px solid #077187"
        />
        <SaveButton
          title="Reset Filters"
          onClick={resetFilters}
          color="white"
          textColor="#077187"
          border="1px solid #077187"
        />
        {downloadEnabled && (
          <SaveButton
            title="Download Excel"
            onClick={handleDownloadExcelClick}
            color="#077187"
            border="1px solid #077187"
          />
        )}
      </div>
    </section>
  );
};

const ChartLegend = ({ data }) => {
  return (
    <section className="participant-reviews-legend">
      <label className="participant-reviews-legend-heading">Legend</label>
      <label className="participant-reviews-legend-heading">Grand Totals</label>
      {data?.results.map((result) => (
        <React.Fragment key={result.name}>
          <div className="participant-reviews-legend-item">
            <span className="participant-reviews-legend-item-tag" style={{ backgroundColor: result.color }} />
            <label>{result.name}</label>
          </div>
          <label className="participant-reviews-legend-item-value">{result.value}</label>
        </React.Fragment>
      ))}
      <div className="participant-reviews-legend-item" />
      <div className="participant-reviews-legend-item">
        <label className="participant-reviews-legend-item-value">{data?.grandTotal ?? 0}</label>
      </div>
    </section>
  );
};

const PiePieceLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value, percent }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text x={x} y={y} fill="#fff" textAnchor="end" fontSize="18" fontWeight="500">
      {percent * 100 > 5 && value}
    </text>
  );
};
